import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { Loader2, FileText, AlertTriangle } from 'lucide-react'

interface Props {
  open: boolean
  onOpenChange: (v: boolean) => void
  existingOrgIds: string[]
}

interface MembershipBlock { blocked: boolean; message?: string; action?: string }

export function PortalApplySheet({ open, onOpenChange, existingOrgIds }: Props) {
  const { toast } = useToast()
  const qc = useQueryClient()
  const [selectedOrg, setSelectedOrg] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const { data: membershipCheck, isFetching: checkingMembership } = useQuery({
    queryKey: ['org-membership-check', selectedOrg],
    enabled: !!selectedOrg,
    queryFn: async (): Promise<MembershipBlock> => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return { blocked: false }

      // Check new accreditation restriction column first
      const { data: orgRow } = await (supabase
        .from('organizations' as any)
        .select('accreditation_restricted_to_association_id, require_membership_number, associations:accreditation_restricted_to_association_id(name, acronym)')
        .eq('id', selectedOrg!)
        .maybeSingle() as any)

      const restrictedAssocId = orgRow?.accreditation_restricted_to_association_id
      if (restrictedAssocId) {
        const assocName = (orgRow?.associations as any)?.name ?? 'the required association'
        const { data: membership } = await (supabase
          .from('association_members' as any)
          .select('status, membership_number')
          .eq('journalist_id', user.id)
          .eq('association_id', restrictedAssocId)
          .eq('status', 'active')
          .maybeSingle() as any)

        if (!membership) {
          return {
            blocked: true,
            message: `This organisation only accredits journalists who are active members of ${assocName}.`,
            action: 'Apply for membership in the required association first.',
          }
        }
        if (orgRow.require_membership_number && (!membership.membership_number || !membership.membership_number.trim())) {
          return {
            blocked: true,
            message: `This organisation requires a valid membership number from ${assocName}.`,
            action: 'Your membership number has not been issued yet. Contact the association.',
          }
        }
      }

      // Legacy: check association_org_links
      const { data: link } = await (supabase
        .from('association_org_links' as any)
        .select('require_membership, association_id')
        .eq('org_id', selectedOrg!)
        .eq('require_membership', true)
        .maybeSingle() as any)

      if (!link) return { blocked: false }

      const { data: membership } = await (supabase
        .from('association_members' as any)
        .select('status, dues_status')
        .eq('journalist_id', user.id)
        .eq('association_id', link.association_id)
        .maybeSingle() as any)

      if (!membership || membership.status !== 'active') {
        return {
          blocked: true,
          message: 'This organisation requires active association membership to apply for accreditation.',
          action: 'Apply for membership first from the Memberships section of your portal.',
        }
      }
      if (membership.dues_status === 'unpaid') {
        return {
          blocked: true,
          message: 'Your association membership dues are unpaid.',
          action: 'Clear your outstanding dues to restore accreditation eligibility.',
        }
      }
      return { blocked: false }
    },
  })

  const isBlocked = membershipCheck?.blocked ?? false

  const { data: kyc } = useQuery({
    queryKey: ['my-kyc-completeness'],
    enabled: open,
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return null
      const { data } = await (supabase
        .from('journalist_profiles' as any)
        .select('avatar_url, national_id_number, doc_national_id, phone, nationality')
        .eq('id', user.id)
        .maybeSingle() as any)
      return data as any
    },
  })

  const kycMissing = kyc ? [
    !kyc.avatar_url && 'Passport-style photo',
    !kyc.national_id_number && 'National ID / passport number',
    !kyc.doc_national_id && 'National ID document upload',
    !kyc.phone && 'Phone number',
    !kyc.nationality && 'Nationality',
  ].filter(Boolean) as string[] : []
  const kycIncomplete = kycMissing.length > 0

  const { data: selectedOrgPolicy } = useQuery({
    queryKey: ['org-accreditation-policy', selectedOrg],
    enabled: !!selectedOrg,
    queryFn: async () => {
      const { data } = await (supabase
        .from('organizations' as any)
        .select('accreditation_policy')
        .eq('id', selectedOrg!)
        .maybeSingle() as any)
      return data?.accreditation_policy as string | null
    },
  })

  const { data: orgs, isLoading } = useQuery({
    queryKey: ['available-orgs-apply'],
    enabled: open,
    queryFn: async () => {
      const { data } = await (supabase
        .from('organizations')
        .select('id, name, brand_primary, brand_accent, logo_url')
        .order('name') as any)
      const all = (data ?? []) as { id: string; name: string; brand_primary: string; brand_accent: string; logo_url?: string | null }[]
      return all.filter(o => !existingOrgIds.includes(o.id))
    },
  })

  const handleSubmit = async () => {
    if (!selectedOrg || isBlocked || kycIncomplete) return
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return
    setSubmitting(true)
    try {
      const { error } = await (supabase
        .from('journalist_accreditations' as any)
        .insert({ journalist_id: user.id, org_id: selectedOrg, status: 'pending' }) as any)
      if (error) throw error
      toast({ title: 'Application submitted', description: 'The organisation will review your request.' })
      setSelectedOrg(null)
      onOpenChange(false)
      qc.invalidateQueries({ queryKey: ['my-accreditations'] })
    } catch (err: any) {
      toast({ title: 'Failed to apply', description: err.message, variant: 'destructive' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Apply for Accreditation</SheetTitle>
          <SheetDescription>
            Select an organisation to request accreditation from. They will review your application and may request supporting documents.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading organisations…
            </div>
          ) : (orgs ?? []).length === 0 ? (
            <p className="text-justify text-sm text-muted-foreground py-6 text-center">
              You have already applied to all available organisations.
            </p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {(orgs ?? []).map(org => (
                <button
                  key={org.id}
                  type="button"
                  onClick={() => setSelectedOrg(org.id)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                    selectedOrg === org.id
                      ? 'border-accent bg-accent/5'
                      : 'border-border hover:border-muted-foreground/40'
                  }`}
                >
                  <OrgPickerLogo name={org.name} logoUrl={org.logo_url} brandColor={org.brand_primary} />
                  <span className="text-sm font-medium">{org.name}</span>
                  {selectedOrg === org.id && (
                    <span className="ml-auto text-accent text-xs font-medium">Selected</span>
                  )}
                </button>
              ))}
            </div>
          )}

          {kycIncomplete && (
            <div className="bg-[hsl(var(--destructive)/0.08)] border border-[hsl(var(--destructive)/0.3)] rounded-lg p-3 space-y-1.5">
              <div className="flex items-center gap-1.5 text-[12px] font-semibold text-destructive">
                <AlertTriangle className="w-3.5 h-3.5" /> Complete your KYC before applying
              </div>
              <p className="text-justify text-[11.5px] text-destructive">
                Your profile is missing: {kycMissing.join(', ')}.
              </p>
              <a href="/portal/profile" className="text-[11.5px] font-medium text-destructive hover:underline">
                Go to My Profile →
              </a>
            </div>
          )}

          {selectedOrg && selectedOrgPolicy && (
            <div className="bg-muted border border-border rounded-lg p-3 space-y-1.5">
              <div className="flex items-center gap-1.5 text-[12px] font-semibold text-foreground">
                <FileText className="w-3.5 h-3.5" /> Accreditation Policy
              </div>
              <p className="text-justify text-[11.5px] text-muted-foreground whitespace-pre-wrap">
                {selectedOrgPolicy}
              </p>
            </div>
          )}

          {isBlocked && membershipCheck && (
            <div className="bg-[hsl(var(--warning)/0.08)] border border-[hsl(var(--warning)/0.3)] rounded-lg p-3 space-y-1">
              <div className="text-[12px] font-semibold text-warning">Membership Required</div>
              <p className="text-justify text-[11.5px] text-warning">{membershipCheck.message}</p>
              <p className="text-justify text-[11.5px] text-warning">{membershipCheck.action}</p>
              <a
                href="/portal/membership"
                className="text-[11.5px] font-medium text-warning hover:underline"
              >
                Go to Memberships →
              </a>
            </div>
          )}

          <Button
            onClick={handleSubmit}
            disabled={!selectedOrg || submitting || isBlocked || checkingMembership || kycIncomplete}
            className="w-full bg-accent hover:bg-accent/90 text-accent-foreground"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            {submitting ? 'Submitting…' : 'Submit Application'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}

function OrgPickerLogo({ name, logoUrl, brandColor }: { name: string; logoUrl?: string | null; brandColor?: string | null }) {
  const [failed, setFailed] = useState(false)
  if (logoUrl && !failed) {
    return (
      <img
        src={logoUrl}
        alt={`${name} logo`}
        className="w-8 h-8 rounded object-contain bg-card border border-border shrink-0"
        onError={() => setFailed(true)}
      />
    )
  }
  return (
    <div
      className="w-8 h-8 rounded flex items-center justify-center text-primary-foreground text-xs font-bold shrink-0"
      style={{ backgroundColor: brandColor ?? 'hsl(var(--foreground))' }}
    >
      {name[0]}
    </div>
  )
}
